import { taxonExpandIri } from './_shared';
import { PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL } from '../../../config/product';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { ASSOCIATION_TYPE, HTTP_METHOD, PRODUCTS_ENDPOINT } from './_shared';

export type MakeGetAssociatedProductsDeps<AssociatedProducts extends SchemaType.Top> = {
  readonly schemas: {
    readonly associatedProducts: AssociatedProducts;
  };
  readonly rawUrlEncode: (input: string) => string;
};

type GetAssociatedProductsProps = {
  productCode: string;
  taxonCode: string | null;
  page: number;
  itemsPerPage: number;
};

export const makeGetAssociatedProducts = <AssociatedProducts extends SchemaType.Top>(
  deps: MakeGetAssociatedProductsDeps<AssociatedProducts>
) => {
  const GetAssociatedProductsError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetAssociatedProducts',
    path: PRODUCTS_ENDPOINT,
    identifierField: 'productCode',
  });

  const getAssociatedProductsFetch = ({
    productCode,
    taxonCode,
    page,
    itemsPerPage,
  }: GetAssociatedProductsProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      const queryParams = {
        page,
        itemsPerPage,
        inStock: true,
        associated_with: productCode,
        association_type: ASSOCIATION_TYPE,
        ...(taxonCode ? { taxon: taxonExpandIri(deps.rawUrlEncode, taxonCode) } : {}),
      };

      return yield* apiClient
        .path(PRODUCTS_ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ query: queryParams });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${PRODUCTS_ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.code': productCode,
          'product.taxonCode': taxonCode ?? PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL,
          'product.page': page,
          'product.itemsPerPage': itemsPerPage,
        },
      })
    );

  const getAssociatedProducts = (props: GetAssociatedProductsProps) =>
    getAssociatedProductsFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.associatedProducts))(response),
          404: (response) =>
            Effect.fail(
              new GetAssociatedProductsError.NotFoundError({
                productCode: props.productCode,
                taxonCode: props.taxonCode ?? PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL,
                response,
              })
            ),
          orElse: (response) =>
            Effect.fail(
              new GetAssociatedProductsError.ApiRequestError({
                productCode: props.productCode,
                taxonCode: props.taxonCode ?? PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL,
                response,
              })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGet,
          'Debug',
          `Successfully resolved ${String(result['hydra:totalItems'])} associated products for ${props.productCode}${props.taxonCode ? ` in taxon ${props.taxonCode}` : ' (all taxons)'}`
        )
      ),
      Effect.withSpan('api.products.getAssociated', {
        kind: 'internal',
        attributes: {
          'product.code': props.productCode,
          'product.taxonCode': props.taxonCode ?? PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL,
          'product.page': props.page,
          'product.itemsPerPage': props.itemsPerPage,
        },
      })
    );

  return { getAssociatedProducts, GetAssociatedProductsError };
};
